import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import { CaptchaWidget, type CaptchaWidgetHandle } from '../../components/common/CaptchaWidget';
import { usePlatformCaptchaConfig } from '../../hooks/useCaptchaConfig';
import { Button } from '../../ui';
import './auth.css';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<CaptchaWidgetHandle | null>(null);

  const { config: captchaConfig, shouldChallenge } = usePlatformCaptchaConfig();
  const captchaRequired = shouldChallenge('admin_forgot_password');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (captchaRequired && !captchaToken) {
      setError('Please complete the CAPTCHA challenge.');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(
        '/api/auth/forgot-password',
        { email, captcha_token: captchaToken ?? undefined },
        { skipAuth: true }
      );
      setIsSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h1>Check Your Email</h1>
          <p className="auth-subtitle">
            If an account exists with <strong>{email}</strong>, you will receive password reset
            instructions shortly.
          </p>
          <div className="auth-footer">
            <Link to="/login" className="auth-link">
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Forgot Password</h1>
        <p className="auth-subtitle">
          Enter your email address and we'll send you instructions to reset your password.
        </p>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="auth-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={isSubmitting}
            />
          </div>

          {captchaRequired && captchaConfig?.provider && captchaConfig.siteKey && (
            <CaptchaWidget
              ref={captchaRef}
              provider={captchaConfig.provider}
              siteKey={captchaConfig.siteKey}
              action="admin_forgot_password"
              onVerify={setCaptchaToken}
              onError={() => setCaptchaToken(null)}
              onExpire={() => setCaptchaToken(null)}
            />
          )}

          <Button type="submit" block size="lg" loading={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send Reset Instructions'}
          </Button>
        </form>

        <div className="auth-footer">
          <Link to="/login" className="auth-link">
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
